import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import { projectsApi, datasetsApi, methodTypesApi } from '../lib/api';
import { supabaseData } from '../lib/supabase';
import Layout from '../components/Layout';
import Breadcrumbs from '../components/Breadcrumbs';
import { useToast } from '../components/Toast';
import { isRasterFileName, splitRasterUpload } from '../lib/raster-api';

const TABULAR_UPLOAD_EXTS = ['csv', 'txt', 'xyz'];
/** CSV / TXT / XYZ uploads get the column-mapping step (.dat is usually a raw survey format). */
export const isTabularUpload = (name) => TABULAR_UPLOAD_EXTS.includes(String(name || '').split('.').pop().toLowerCase());

// Keep in step with backend MAX_UPLOAD_BYTES (default 200 MB). The server
// enforces the real limit; this just fails fast before any request is made.
const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;

function Upload() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { selectedOrgId, loading: orgLoading } = useOrg();
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [methodTypes, setMethodTypes] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [datasetName, setDatasetName] = useState('');
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);  // Support multiple files
  const [worldFile, setWorldFile] = useState(null);  // .pgw/.jgw/... sent with a raster image
  const [rasterCrs, setRasterCrs] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mapLink, setMapLink] = useState(null); // CSV/XYZ: "Set columns now" opens the map's Column Mapping (V.26)

  useEffect(() => {
    loadMethodTypes();
  }, []);

  useEffect(() => {
    if (selectedOrgId) {
      loadProjects(selectedOrgId);
    } else {
      setProjects([]);
      setSelectedProject('');
      setProjectsLoading(false);
    }
  }, [selectedOrgId]);

  const loadMethodTypes = async () => {
    try {
      const methodsRes = await methodTypesApi.list();
      setMethodTypes(methodsRes.data.methods);
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    }
  };

  const loadProjects = async (orgId) => {
    setProjectsLoading(true);
    try {
      const res = await projectsApi.list(orgId);
      setProjects(res.data);
      if (res.data.length > 0) {
        setSelectedProject(res.data[0].id);
      }
    } catch (err) {
      setError('Failed to load projects: ' + err.message);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    selectFiles(selectedFiles);
  };

  // First non-world-file is the primary file; a world file rides along (raster images).
  const selectFiles = (selectedFiles) => {
    if (selectedFiles.length === 0) return;
    const { primary, worldFile: wf } = splitRasterUpload(selectedFiles);
    const first = primary || selectedFiles[0];
    // One dataset = one file; the only companion is a raster world file.
    setFiles(wf ? [first, wf] : [first]);
    setFile(first);
    setWorldFile(wf);
    if (wf && !rasterCrs) setRasterCrs('EPSG:4326'); // GeoreferencingTool world files are lon/lat
    if (!datasetName) {
      setDatasetName(first.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    selectFiles(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !selectedProject || !selectedMethod || !datasetName) {
      setError('Please fill in all fields');
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      const message = `${file.name} is ${(file.size / (1024 * 1024)).toFixed(1)} MB, which exceeds the ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB upload limit.`;
      setError(message);
      showToast(message, 'error', 6000);
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setMapLink(null);
    setUploadProgress(0);

    try {
      // Step 1: Request upload (creates dataset)
      console.log('Step 1: Requesting upload URL...');
      const urlResponse = await datasetsApi.requestUploadUrl({
        filename: file.name,
        file_type: file.type,
        file_size: file.size,
        project_id: selectedProject,
        dataset_name: datasetName,
        method_type: selectedMethod
      });

      const { file_path, dataset_id, upload_token } = urlResponse.data;
      console.log('Upload metadata:', { file_path, dataset_id, hasToken: !!upload_token });
      setUploadProgress(30);

      // Step 2: Upload file to Supabase Storage
      console.log('Step 2: Uploading to Supabase Storage...');
      console.log('Upload path:', file_path);
      console.log('File details:', { name: file.name, size: file.size, type: file.type });

      if (!upload_token) {
        throw new Error('Upload authorization failed — the server did not return an upload token');
      }

      // Signed upload — the backend authorized this path with the service
      // role, so the browser upload bypasses storage RLS entirely.
      const { data: uploadData, error: uploadError } = await supabaseData.storage
        .from('datasets')
        .uploadToSignedUrl(file_path, upload_token, file, { cacheControl: '3600' });

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        const msg = uploadError.message || JSON.stringify(uploadError);
        // Supabase Storage's project-wide "Upload file size limit" (50 MB by default) is
        // checked by storage itself, below the app's own max_upload_bytes cap.
        const hint = /exceeded the maximum allowed size/i.test(msg)
          ? ' The storage service rejected the file size (Supabase project upload limit, 50 MB by default): raise it in the Supabase dashboard under Project Settings > Storage, or shrink the file — for a GeoTIFF, recompress with DEFLATE and predictor 3.'
          : '';
        throw new Error(`Storage error: ${msg}${hint}`);
      }

      console.log('Upload successful:', uploadData);
      setUploadProgress(70);

      // Verify file was uploaded
      const { data: listData } = await supabaseData.storage
        .from('datasets')
        .list(dataset_id);
      
      console.log('Files in bucket:', listData);

      // Step 3: Complete upload (create dataset version)
      console.log('Step 3: Completing upload...');
      
      // Get file type or infer from extension
      let fileType = file.type || '';
      if (!fileType) {
        const ext = file.name.split('.').pop().toLowerCase();
        const mimeTypes = {
          'stg': 'application/x-agi-sting',
          'trn': 'application/x-agi-terrain',
          'csv': 'text/csv',
          'txt': 'text/plain',
          'xyz': 'text/plain',
          'dat': 'application/octet-stream',
          'tif': 'image/tiff',
          'tiff': 'image/tiff',
        };
        fileType = mimeTypes[ext] || 'application/octet-stream';
        console.log(`Inferred file type: ${fileType} from extension: ${ext}`);
      }
      
      const formData = new FormData();
      formData.append('dataset_id', dataset_id);
      formData.append('file_path', file_path);
      formData.append('file_size', file.size.toString());
      formData.append('file_type', fileType);
      // Raster image + world file (docs/RASTER_SUPPORT.md): the server queues a raster_ingest job.
      if (worldFile) formData.append('world_file', await worldFile.text());
      if (rasterCrs.trim() && (worldFile || isRasterFileName(file.name))) formData.append('raster_crs', rasterCrs.trim());

      console.log('Sending to backend:', {
        dataset_id,
        file_path,
        file_size: file.size,
        file_type: fileType
      });

      const completeResponse = await datasetsApi.completeUpload(formData);
      console.log('Upload complete:', completeResponse.data);
      setUploadProgress(100);

      if (isTabularUpload(file.name)) {
        // Tabular files need X/Y/Z columns and a CRS before they can be drawn: offer that step now
        // (the map's Column Mapping saves it on the dataset) instead of redirecting away.
        setSuccess('✅ File uploaded. Set the X/Y/Z columns and coordinate system to place it on the map.');
        setMapLink(`/map?project=${encodeURIComponent(selectedProject)}&dataset=${encodeURIComponent(dataset_id)}`);
        setFile(null);
        setUploadProgress(0);
        return;
      }

      setSuccess(`✅ File uploaded successfully! Redirecting...`);
      
      setTimeout(() => {
        setFile(null);
        setDatasetName('');
        setUploadProgress(0);
        navigate('/datasets');
      }, 1500);

    } catch (err) {
      console.error('Upload error:', err);
      
      // Better error parsing
      let errorMessage = 'Failed to upload file';
      
      if (err.response?.data?.detail) {
        const d = err.response.data.detail;
        errorMessage = typeof d === 'string' ? d : JSON.stringify(d);
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.error) {
        errorMessage = err.error;
      } else if (typeof err === 'object') {
        errorMessage = JSON.stringify(err);
      }
      
      setError(`Upload failed: ${errorMessage}`);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  if (!orgLoading && !selectedOrgId) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumbs />
          <h1 className="text-3xl font-bold mb-6">Upload Data</h1>
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">Select or create an organization to upload data</p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-md hover:opacity-90"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <h1 className="text-3xl font-bold mb-6">Upload Data</h1>

        <div className="max-w-3xl">
          <div className="bg-card shadow-lg rounded-lg p-8 border border-border">
            <h2 className="text-xl font-semibold mb-6">Upload Geophysical Survey Data</h2>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                {success}
                {mapLink && (
                  <div className="mt-2 flex gap-4">
                    <Link to={mapLink} className="font-medium underline">Set columns now</Link>
                    <Link to="/datasets" className="underline">Go to Datasets</Link>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleUpload} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Project</label>
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                  disabled={!selectedOrgId || projectsLoading || projects.length === 0}
                  data-testid="project-select"
                  aria-busy={projectsLoading}
                >
                  <option value="">{projectsLoading ? 'Loading projects…' : 'Select project...'}</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
                {selectedOrgId && !projectsLoading && projects.length === 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    No projects yet. <Link to="/projects" className="text-secondary hover:underline">Create one first</Link>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Dataset Name</label>
                <input
                  type="text"
                  value={datasetName}
                  onChange={(e) => setDatasetName(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Enter dataset name"
                  required
                  data-testid="dataset-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Geophysical Method</label>
                <select
                  value={selectedMethod}
                  onChange={(e) => setSelectedMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                  data-testid="method-select"
                >
                  <option value="">Select method...</option>
                  {methodTypes.map(method => (
                    <option key={method.value} value={method.value}>{method.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Data File</label>
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className="w-full px-6 py-8 border-2 border-dashed border-input rounded-md hover:border-primary transition-colors cursor-pointer bg-muted/30"
                  data-testid="drop-zone"
                >
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    required
                    multiple
                    id="file-input"
                    data-testid="file-input"
                    accept=".csv,.txt,.stg,.cmd,.trn,.gpd,.xyz,.dat,.tif,.tiff,.asc,.png,.jpg,.jpeg,.pgw,.jgw,.tfw,.wld,.las,.tsv"
                  />
                  <label htmlFor="file-input" className="cursor-pointer block text-center">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-muted-foreground mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-sm font-medium mb-1">
                        {files.length > 0 ? `${files.length} file(s) selected` : 'Drop your files here or click to browse'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {files.length > 0 
                          ? `Primary: ${files[0].name} (${(files[0].size / 1024).toFixed(2)} KB)` 
                          : 'Supports: CSV, STG, TRN, XYZ, DAT and more'
                        }
                      </p>
                    </div>
                  </label>
                </div>
                {files.length > 1 && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-xs font-semibold text-blue-800 mb-2">
                      📁 {files.length} files selected:
                    </p>
                    <ul className="text-xs text-blue-700 space-y-1">
                      {files.map((f, idx) => (
                        <li key={idx}>
                          • {f.name} ({(f.size / 1024).toFixed(2)} KB)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {file && (isRasterFileName(file.name) || worldFile) && (
                  <div className="mt-3 p-3 border border-border rounded-md text-sm" data-testid="raster-upload-options">
                    <p className="text-xs text-muted-foreground mb-2">
                      Raster: converted to a Cloud-Optimized GeoTIFF in the background.
                      {worldFile ? ` World file: ${worldFile.name}.` : ' PNG/JPEG images need a world file (Georeferencing tool export).'}
                    </p>
                    <label className="text-xs font-medium mr-2" htmlFor="raster-crs">CRS {worldFile ? '' : '(only if the file has none)'}</label>
                    <input id="raster-crs" value={rasterCrs} onChange={(e) => setRasterCrs(e.target.value)}
                      placeholder="e.g. EPSG:4326" className="px-2 py-1 text-xs border border-input rounded-md bg-background" />
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  💡 One file per dataset. Raster images can be selected together with their world file (.pgw, .jgw, .tfw, .wld).
                </p>
              </div>

              {uploading && uploadProgress > 0 && (
                <div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Uploading... {uploadProgress}%
                  </p>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={uploading || !file}
                  className="flex-1 bg-primary text-primary-foreground py-2 px-4 rounded-md hover:opacity-90 transition disabled:opacity-50"
                  data-testid="upload-button"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/datasets')}
                  className="px-6 py-2 border border-border rounded-md hover:bg-muted transition"
                  data-testid="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Upload;
